/* eslint-disable import/prefer-default-export */
import React from 'react'
import { DashboardWidgetData } from 'features/widget/types'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import {
  Facebook,
  Instagram,
  Reddit,
  Twitter,
  LexisNexis,
  Weibo,
  Msn,
  Tiktok,
  VocSmall,
  YoutubeCircle
  // @ts-ignore
} from '@netbasequid/core'
import { Domain, InsightViewOption, MessageConst } from 'enums'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { Theme } from 'features/ui/types'
import { InsightViewOptionStrings } from 'enums/InsightViewOption'
import StreamSymbol from 'enums/StreamSymbol'
import { AuthorsWidgetsType } from '../../types'

const retrieveDataSet = (response: DashboardWidgetData<InsightCount>, topN: number) => {
  const topNDataSet = response?.results?.[0]?.data?.insights?.[0]?.dataset?.map(
    (insightCategory) => ({
      ...insightCategory,
      set: insightCategory.set?.slice(0, topN)
    })
  )
  const mergedTopNDataset =
    topNDataSet?.flatMap((insightCategory) =>
      insightCategory.set!.map((item) => ({ ...item, insightType: insightCategory.insightType }))
    ) || []
  return mergedTopNDataset
}

export const getNumberFormatter = () =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })

const sortByViewOption = (data: InsightData[], viewOption: InsightViewOptionStrings) => {
  let field: 'engagements' | 'mentions' | 'followers' = 'mentions'

  switch (viewOption) {
    case InsightViewOption.MOST_ENGAGEMENT:
      field = 'engagements'
      break
    case InsightViewOption.MOST_MENTIONS:
      field = 'mentions'
      break
    case InsightViewOption.MOST_FOLLOWERS:
      field = 'followers'
      break
    default:
      break
  }

  return data.sort((a, b) => {
    const itemA = a[field]
    const itemB = b[field]

    if (itemA && itemB) {
      return itemB - itemA
    }
    return 1
  })
}

export const handleEngagementsDisplay = (data: InsightData) => {
  const isSupportedDomain = [
    Domain.FACEBOOK,
    Domain.TWITTER,
    Domain.INSTAGRAM,
    Domain.YOUTUBE,
    Domain.TIKTOK,
    Domain.REDDIT
  ].some((item) => item === data.domain)
  if (isSupportedDomain && data.engagements) {
    return getNumberFormatter().format(data.engagements)
  }
  return MessageConst.EM_DASH
}

/**
 * convert responses from runsearch to data displayed on table
 * @param searchResponses responses from runsearch
 * @param widgetSetting widget setting
 */
export const convertToTableData = (
  searchResponses: DashboardWidgetData<InsightCount>[],
  widgetSetting: AuthorsWidgetsType
) => {
  const rowData =
    searchResponses
      ?.map((response) => {
        const dataSet = retrieveDataSet(response, widgetSetting.topN!)
        return sortByViewOption(dataSet, widgetSetting.viewOption!)
      })
      .flat() || []
  return rowData
}

export const getDataSource = (row: InsightData) => {
  switch (row.dataStreams?.[0]) {
    case 'VOC':
      return StreamSymbol.VOC
    case 'LXNX':
      return StreamSymbol.LXNX
    default:
      break
  }
  return row.domain
}

export const getSourceLogo = (source: string | undefined, mode?: Theme) => {
  switch (source) {
    case Domain.FACEBOOK:
      return <Facebook mode={mode} />
    case Domain.TWITTER:
      return <Twitter mode={mode} />
    case Domain.INSTAGRAM:
      return <Instagram mode={mode} />
    case Domain.WEIBO:
      return <Weibo mode={mode} />
    case Domain.REDDIT:
      return <Reddit mode={mode} />
    case Domain.MSN:
      return <Msn mode={mode} />
    case Domain.TIKTOK:
      return <Tiktok mode={mode} />
    case Domain.YOUTUBE:
      return <YoutubeCircle mode={mode} />
    case 'VOC':
      return <VocSmall mode={mode} />
    case 'LXNX':
      return <LexisNexis mode={mode} />
    default:
      return null
  }
}

export const getTooltipContent = (source: string | undefined, row: InsightData) => {
  switch (source) {
    case 'VOC':
      return row.domain ? `VoC: ${row.domain}` : 'VoC'
    case 'LXNX':
      return 'LexisNexis'
    default:
      return source
  }
}
